import React, { useState } from "react";
import { ChevronRight, ArrowLeft } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import { Vehicle } from "./FleetManagementPage";
import { useGetFleetByIdQuery, useUpdateFleetMutation } from "../../redux/services/fleetManagement";
import VehicleIcons from "./VehicleIcons";
import { toast } from "sonner";
import { useStaffController } from "../../controllers/useStaffController";
import { usePermission } from "../../utils/permissions";

const BRAND = "#143887";
const BRAND_LIGHT = "#1438871A";

interface Props {
  vehicle: Vehicle;
  onBack: () => void;
  onAssignDriver: () => void;
}

export default function VehicleDetailsPage({ vehicle, onBack, onAssignDriver }: Props) {
  const { can } = usePermission();
  const canEditFleet = can("edit fleet");
  const { data } = useGetFleetByIdQuery({
    fleet_id: vehicle?.fleet_id || "",
  });
  const [updateFleet, { isLoading: updating }] = useUpdateFleetMutation();
  const { staffData } = useStaffController();

  const [formData, setFormData] = useState({
    vehicleName: vehicle.model,
    plateNumber: vehicle.licensePlate,
    vinNumber: vehicle.vin,
    type: vehicle.type,
    capacity: vehicle.capacity,
    status: vehicle.status,
    driver: vehicle.userId,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleUpdateVehicle = async () => {
    if (!canEditFleet) {
      toast.error("You don't have permission to edit fleet");
      return;
    }
    try {
      const payload = {
        model_id: formData.vehicleName,
        license_plate: formData.plateNumber,
        vin: formData.vinNumber,
        vehicle_type: formData.type,
        capacity: formData.capacity,
        status: formData.status,
        user_id: formData.driver,
      };

      console.log("Updating vehicle with payload:", payload);

      await updateFleet({
        fleet_id: vehicle.fleet_id,
        data: payload,
      }).unwrap();

      toast.success("Vehicle updated successfully");
    } catch (error: any) {
      console.error(error);

      toast.error(
        error?.data?.message ||
        error?.message ||
        "Failed to update vehicle"
      );
    }
  };

  const breadcrumb = (
    <div className="flex items-center gap-2 text-sm">
      <button
        onClick={onBack}
        className="font-semibold hover:underline"
        style={{ color: BRAND }}
      >
        Fleet Management
      </button>
      <ChevronRight size={14} className="text-gray-400" />
      <span className="text-gray-600 font-medium">Vehicle details</span>
    </div>
  );
  console.log("vehicle data", data);

  return (
    <DashboardLayout title={breadcrumb}>
      <div className="w-full space-y-5 mb-10">

        {/* Back button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors hover:bg-blue-50"
          style={{ borderColor: BRAND, color: BRAND }}
        >
          <ArrowLeft size={16} />
          Back to Fleet Management
        </button>

        {/* Top row: image + map */}
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-5">
          {/* Vehicle image placeholder */}
          <div
          className="bg-white rounded-xl border border-gray-100 shadow-sm flex items-center justify-center py-10 relative overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 1px 1px, #e5e7eb 1px, transparent 0),
              linear-gradient(to right, rgba(20,56,135,0.03), rgba(20,56,135,0.01))
            `,
            backgroundSize: "18px 18px, 100% 100%",
          }}
        >
          <div className="flex flex-col items-center gap-4 relative z-10">
            <div className="p-4 rounded-2xl bg-white/80 backdrop-blur-sm shadow-sm">
              <VehicleIcons vehicleType={vehicle.type} />
            </div>

            <p className="text-sm font-medium text-gray-700 text-center">
              {vehicle.model}
            </p>

            <p className="text-xs text-gray-400">
              {vehicle.type}
            </p>
          </div>
        </div>

          {/* Map placeholder */}
          {/* <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <p className="text-xs font-semibold text-gray-600 mb-3">Real-time Location & Tracking</p>
            <div className="rounded-lg overflow-hidden relative" style={{ height: 150, background: "#dce8fb" }}>
              <div className="absolute inset-0 flex items-center justify-center flex-col gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: BRAND }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="white" />
                    <circle cx="12" cy="9" r="2.5" fill={BRAND} />
                  </svg>
                </div>
                <p className="text-xs text-gray-500">{vehicle.lastLocation}</p>
              </div>
              {[{ top: "20%", left: "25%" }, { top: "55%", left: "65%" }, { top: "35%", left: "50%" }].map((pos, i) => (
                <div
                  key={i}
                  className="absolute w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold"
                  style={{ background: BRAND, top: pos.top, left: pos.left }}
                >
                  {vehicle.id.replace("#", "").slice(0, 2)}
                </div>
              ))}
            </div>
          </div> */}
        </div>

        {/* Vehicle Overview + Driver Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Vehicle Overview */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-5">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center"
                style={{ background: BRAND_LIGHT }}
              >
                🚘
              </div>
              <p className="text-sm font-semibold text-gray-700">
                Vehicle Overview
              </p>
            </div>

            <div className="space-y-4">

              {/* Vehicle Name */}
              <div className="flex justify-between items-center">
                <label className="text-xs text-gray-500 block">
                  Vehicle Name
                </label>
                <input
                  name="vehicleName"
                  value={formData.vehicleName}
                  onChange={handleChange}
                  className="w-1/2 border border-gray-200 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1"
                />
              </div>

              {/* Plate Number */}
              <div className="flex justify-between items-center">
                <label className="text-xs text-gray-500 block">
                  Plate Number
                </label>
                <input
                  name="plateNumber"
                  value={formData.plateNumber}
                  onChange={handleChange}
                  className="w-1/2 border border-gray-200 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1"
                />
              </div>

              {/* VIN */}
              <div className="flex justify-between items-center">
                <label className="text-xs text-gray-500 block">
                  VIN Number
                </label>
                <input
                  name="vinNumber"
                  value={formData.vinNumber}
                  onChange={handleChange}
                  className="w-1/2 border border-gray-200 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1"
                />
              </div>

              {/* Type */}
              <div className="flex justify-between items-center">
                <label className="text-xs text-gray-500 block">
                  Vehicle Type
                </label>
                <div className="relative w-1/2">
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-md px-3 py-1 text-sm focus:outline-none appearance-none bg-white"
                  >
                    <option value="Car">Car</option>
                    <option value="Van">Van</option>
                    <option value="Bike">Bike</option>
                    <option value="Lorry">Truck</option>
                  </select>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">▾</span>
                </div>
              </div>

              {/* Capacity */}
              <div className="flex justify-between items-center">
                <label className="text-xs text-gray-500 block">
                  Capacity
                </label>
                <input
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                  className="w-1/2 border border-gray-200 rounded-md px-3 py-1 text-sm focus:outline-none"
                />
              </div>

              {/* Status */}
              <div className="flex justify-between items-center">
                <label className="text-xs text-gray-500 block">
                  Status
                </label>
                <div className="relative w-1/2">
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-md px-3 py-1 text-sm focus:outline-none appearance-none bg-white"
                  >
                    <option value="Assigned">Assigned</option>
                    <option value="Pending">Pending</option>
                    <option value="Un-Assigned">Un-Assigned</option>
                  </select>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">▾</span>
                </div>
              </div>

              {/* <div className="flex justify-between items-center text-sm">
                <span className="text-xs text-gray-500">Last Location</span>
                <span>{vehicle.lastLocation}</span>
              </div> */}
              <div className="flex justify-between items-center text-sm">
                <span className="text-xs text-gray-500">Last Updated</span>
                <span>{vehicle.lastUpdated}</span>
              </div>

            </div>
          </div>

          {/* Driver Information */}
          <div className="bg-white flex flex-col justify-between rounded-xl border border-gray-100 shadow-sm p-5">
            <div>
              <div className="flex items-center gap-2 mb-5">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ background: BRAND_LIGHT }}
                >
                  👨‍✈️
                </div>
                <p className="text-sm font-semibold text-gray-700">
                  Driver Information
                </p>
              </div>

              <div className="space-y-4 mb-6">

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Current Driver</span>
                  <span className="font-medium">{vehicle.assignedDriver}</span>
                </div>

                {/* <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Average Rating</span>
                  <span className="font-medium">{vehicle.avgRating}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Completed Trips</span>
                  <span className="font-medium">{vehicle.completedTrips}</span>
                </div> */}

                {/* Reassign Driver */}
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">
                    Reassign Driver
                  </label>
                  <div className="relative">
                    <select
                      name="driver"
                      value={formData.driver}
                      onChange={handleChange}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none appearance-none bg-white"
                    >
                      <option value="">Select new driver</option>
                      {staffData.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">▾</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Update Button */}
            <button
              onClick={handleUpdateVehicle}
              disabled={updating || !canEditFleet}
              title={!canEditFleet ? "You don't have permission to edit fleet" : undefined}
              className="w-full py-3 rounded-lg text-white font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: BRAND }}
            >
              {updating ? "Updating..." : "Update Vehicle Details"}
            </button>
          </div>
        </div>

        {/* Trip Logs */}
        {/* <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-50">
            <p className="text-sm font-semibold text-gray-700">Trip Logs</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-50 text-gray-500">
                  {["Date", "Trip Starts", "Trip Ends", "Distance Covered", "Time Duration", "Status"].map((col) => (
                    <th key={col} className="text-left px-5 py-3 font-medium">
                      <span className="flex items-center gap-1">{col} <ChevronRight size={10} /></span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vehicle.tripLogs.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-gray-400">No trip logs available</td></tr>
                ) : (
                  vehicle.tripLogs.map((log, i) => (
                    <tr key={i} className="border-b border-gray-50 last:border-0">
                      <td className="px-5 py-3 text-gray-600">{log.date}</td>
                      <td className="px-5 py-3 text-gray-600">{log.tripStarts}</td>
                      <td className="px-5 py-3 text-gray-600">{log.tripEnds}</td>
                      <td className="px-5 py-3 text-gray-600">{log.distance}</td>
                      <td className="px-5 py-3 text-gray-600">{log.duration}</td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[log.status]}`}>
                          {log.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 text-xs text-gray-400 border-t border-gray-50">Pages 1 of 1</div>
        </div> */}

        {/* Maintenance + Payment */}
        {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 pb-8"> */}
          {/* Maintenance History */}
          {/* <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="px-5 py-4 border-b border-gray-50">
              <p className="text-sm font-semibold text-gray-700">Maintenance History</p>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-50 text-gray-500">
                  {["Date Performed", "Type", "Cost"].map((col) => (
                    <th key={col} className="text-left px-5 py-3 font-medium">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vehicle.maintenanceHistory.length === 0 ? (
                  <tr><td colSpan={3} className="text-center py-8 text-gray-400">No maintenance records</td></tr>
                ) : (
                  vehicle.maintenanceHistory.map((rec, i) => (
                    <tr key={i} className="border-b border-gray-50 last:border-0">
                      <td className="px-5 py-3 text-gray-600">{rec.datePerformed}</td>
                      <td className="px-5 py-3 text-gray-600">{rec.type}</td>
                      <td className="px-5 py-3 text-gray-600">{rec.cost}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div> */}

          {/* Payment Information */}
          {/* <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <p className="text-sm font-semibold text-gray-700 mb-4">Payment Information</p>
            <div className="space-y-3">
              {[
                { label: "Payment By:", value: "Sender" },
                { label: "Payment Method:", value: "Invoice" },
                { label: "Billing Address:", value: "456 Broadway Ave, LG" },
                { label: "Discount Applied:", value: "20% Promo" },
              ].map((row, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: BRAND }} />
                  <span className="text-gray-500">{row.label}</span>
                  <span className="text-gray-800 font-medium ml-auto">{row.value}</span>
                </div>
              ))}
            </div>
          </div> */}
        {/* </div> */}

      </div>
    </DashboardLayout>
  );
}